import { baseURL } from '../utils/constants';
import {
  CancelAnalyticsParams,
  CancelReasonAnalyticsResponse,
  DoctorAttendanceParams,
  DoctorAttendanceResponse,
  DownloadRequest,
  DownloadRequestResponse,
  DownloadRequestsResponse,
  DownloadSignedUrlResponse,
  GetDownloadRequestsParams,
  GoogleReviewParams,
  GoogleReviewResponse,
  RatingAnalyticsParams,
  RatingAnalyticsResponse,
  RCCountAnalyticsParams,
  RCCountAnalyticsResponse,
  SearchDownloadRequestsParams,
} from "../utils/types";
import { apiCallWithRetry } from '../utils/apiRetry';

export interface BookingAnalyticsParams {
  hospital_id: string;
  doctor_id?: string;
  start_date?: string;
  end_date?: string;
  hospital_name?: string;
}

export interface DailyData {
  date: string;
  hour_label?:string
  "Dashboard Appointments": number;
  "Whatsapp Appointments": number;
  "Walk-in Bookings": number;
  "Follow-up Bookings": number;
}

export interface BookingAnalyticsResponse {
  daily_data: DailyData[];
  totals: {
    "Dashboard Appointments": number;
    "Whatsapp Appointments": number;
    "Walk-in Bookings": number;
    "Follow-up Bookings": number;
  };
}

export interface LeadsAnalyticsResponse {
  funnel: {
    started: number;
    date_selected: number;
    doctor_selected: number;
    session_selected: number;
    booking_confirmed: number;
  }
}

export interface LeadsCardsResponse {
  total_bookings: string;
  new_patients: string;
  reviewed_bookings: string;
  showed_up: string;
  pct_total_bookings: number;
  pct_reviewed_bookings: number;
  pct_new_patients: number;
  pct_showed_up: number;
}



export const getBookingAnalytics = async (params: BookingAnalyticsParams): Promise<BookingAnalyticsResponse> => {
  try {
    const { data } = await apiCallWithRetry({
      method: 'GET',
      url: `${baseURL}/analytics/advance/booking`,
      params: {
        hospital_id: params.hospital_id,
        ...(params.doctor_id && { doctor_id: params.doctor_id }),
        ...(params.start_date && { start_date: params.start_date }),
        ...(params.end_date && { end_date: params.end_date }),
      },
    });
    console.log(data);
    
    return data.result;
  } catch (error) {
    console.error('Error fetching booking analytics:', error);
    throw error;
  }
};

export const getLeadsAnalytics = async (params: BookingAnalyticsParams): Promise<LeadsAnalyticsResponse> => {
  try {
    const { data } = await apiCallWithRetry({
      method: 'GET',
      url: `${baseURL}/analytics/advance/leads`,
      params: {
        hospital_id: params.hospital_id,
        ...(params.start_date && { start_date: params.start_date }),
        ...(params.end_date && { end_date: params.end_date }),
      },
    });
    
    if (data.status === "ERROR") {
      throw new Error(data.error || "Failed to fetch leads analytics");
    }
    
    return data.result;
  } catch (error) {
    console.error('Error fetching leads analytics:', error);
    throw error;
  }
};

export const getLeadsCardsData= async (params: BookingAnalyticsParams): Promise<LeadsCardsResponse> => {
  try {
    const { data } = await apiCallWithRetry({
      method: 'GET',
      url: `${baseURL}/analytics/advance/leads/cards`,
      params: {
        hospital_id: params.hospital_id,
        ...(params.start_date && { start_date: params.start_date }),
        ...(params.end_date && { end_date: params.end_date }),
      },
    });

    if (data.status === "ERROR") {
      throw new Error(data.error || "Failed to fetch leads analytics");
    }
    console.log(data.result);
    
    return data.result;
  } catch (error) {
    console.error('Error fetching leads analytics:', error);
    throw error;
  }
};

export const getCancelreasonAnalytics = async (params: CancelAnalyticsParams): Promise<CancelReasonAnalyticsResponse> => {
  try {
    const { data } = await apiCallWithRetry({
      method: 'GET',
      url: `${baseURL}/analytics/advance/cancel-reason`,
      params: {
        hospital_id: params.hospital_id,
        ...(params.doctor_id && { doctor_id: params.doctor_id }),
        ...(params.start_date && { start_date: params.start_date }),
        ...(params.end_date && { end_date: params.end_date }),
      },
    });

    if (data.status !== "SUCCESS") {
      throw new Error(data.error || "Failed to fetch cancel reason analytics");
    }
    
    console.log('Cancel reason analytics:', data.result);
    return data.result;
  } catch (error) {
    console.error('Error fetching cancel reason analytics:', error);
    throw error;
  }
};

export const getRescheduledAndCancelledAnalytics = async (params: RCCountAnalyticsParams): Promise<RCCountAnalyticsResponse> => {
  try {
    const { data } = await apiCallWithRetry({
      method: 'GET',
      url: `${baseURL}/analytics/advance/rc-count`,
      params: {
        hospital_id: params.hospital_id,
        ...(params.doctor_id && { doctor_id: params.doctor_id }),
        ...(params.start_date && { start_date: params.start_date }),
        ...(params.end_date && { end_date: params.end_date }),
      },
    });

    if (data.status !== "SUCCESS") {
      throw new Error(data.error || "Failed to fetch rescheduled and cancelled analytics");
    }
    
    return data.result;
  } catch (error) {
    console.error('Error fetching rescheduled and cancelled analytics:', error);
    throw error;
  }
}

export const getDoctorAttendanceAnalytics = async (params: DoctorAttendanceParams): Promise<DoctorAttendanceResponse> => {
  try {
    const { data } = await apiCallWithRetry({
      method: 'GET',
      url: `${baseURL}/analytics/advance/doctor-attendence`,
      params: {
        hospital_id: params.hospital_id,
        doctor_mapping_id: params.mapping_id,
        ...(params.start_date && { start_date: params.start_date }),
        ...(params.end_date && { end_date: params.end_date }),
      },
    });

    if (data.status !== "SUCCESS") {
      throw new Error(data.error || "Failed to fetch doctor attendance analytics");
    }
    
    console.log('Doctor attendance analytics:', data.result);
    return data.result;
  } catch (error) {
    console.error('Error fetching doctor attendance analytics:', error);
    throw error;
  }
};

export const getGoogleReviewAnalytics = async (params: GoogleReviewParams): Promise<GoogleReviewResponse> => {
  try {
    const { data } = await apiCallWithRetry({
      method: 'GET',
      url: `${baseURL}/analytics/advance/google-review`,
      params: {
        hospital_id: params.hospital_id,
        ...(params.doctor_id && { doctor_id: params.doctor_id }),
        ...(params.start_date && { start_date: params.start_date }),
        ...(params.end_date && { end_date: params.end_date }),
      },
    });

    if (data.status !== "SUCCESS") {
      throw new Error(data.error || "Failed to fetch Google review analytics");
    }
    
    console.log('Google review analytics:', data.result);
    return data.result;
  } catch (error) {
    console.error('Error fetching Google review analytics:', error);
    throw error;
  }
};


export const getRatingAnalytics = async (params: RatingAnalyticsParams): Promise<RatingAnalyticsResponse> => {
  try {
    const { data } = await apiCallWithRetry({
      method: 'GET',
      url: `${baseURL}/analytics/advance/rating`,
      params: {
        hospital_id: params.hospital_id,
        ...(params.doctor_id && { doctor_id: params.doctor_id }),
        ...(params.start_date && { start_date: params.start_date }),
        ...(params.end_date && { end_date: params.end_date }),
      },
    });

    if (data.status !== "SUCCESS") {
      throw new Error(data.error || "Failed to fetch rating analytics");
    }
    
    console.log('Rating analytics:', data.result);
    return data.result;
  } catch (error) {
    console.error('Error fetching rating analytics:', error);
    throw error;
  }
};

//Download Excel - New Async Queue-Based System

// Request download (returns 202 Accepted)
export const requestBookingDownload = async (params: BookingAnalyticsParams): Promise<DownloadRequestResponse> => {
  try {
    const response = await apiCallWithRetry({
      method: 'GET',
      url: `${baseURL}/analytics/advance/booking/download`,
      params: {
        hospital_id: params.hospital_id,
        ...(params.doctor_id && { doctor_id: params.doctor_id }),
        ...(params.start_date && { start_date: params.start_date }),
        ...(params.end_date && { end_date: params.end_date }),
      },
    });
    // Expect 202 Accepted
    if (response.status !== 202) {
      throw new Error('Unexpected response status');
    }
    return response.data;
  } catch (error) {
    console.error('Error requesting booking download:', error);
    throw error;
  }
};

// Legacy function - kept for backward compatibility but now uses async endpoint
export const downloadFullBookingData = async (params: BookingAnalyticsParams): Promise<DownloadRequestResponse> => {
  try {
    return await requestBookingDownload(params);
  } catch (error) {
    console.error('Error downloading full booking data:', error);
    throw error;
  }
};

export const requestTotalBookingsDownload = async (params: BookingAnalyticsParams): Promise<DownloadRequestResponse> => {
  try {
    const response = await apiCallWithRetry({
      method: 'GET',
      url: `${baseURL}/analytics/advance/totalbookings/download`,
      params: {
        hospital_id: params.hospital_id,
        ...(params.doctor_id && { doctor_id: params.doctor_id }),
        ...(params.start_date && { start_date: params.start_date }),
        ...(params.end_date && { end_date: params.end_date }),
      },
    });
    if (response.status !== 202) {
      throw new Error('Unexpected response status');
    }
    return response.data;
  } catch (error) {
    console.error('Error requesting total bookings download:', error);
    throw error;
  }
};

export const downloadTotalBookingsData = async (params: BookingAnalyticsParams): Promise<DownloadRequestResponse> => {
  try {
    return await requestTotalBookingsDownload(params);
  } catch (error) {
    console.error('Error downloading total bookings data:', error);
    throw error;
  }
};

export const requestLeadsDownload = async (params: BookingAnalyticsParams): Promise<DownloadRequestResponse> => {
  try {
    const response = await apiCallWithRetry({
      method: 'GET',
      url: `${baseURL}/analytics/advance/leads/download`,
      params: {
        hospital_id: params.hospital_id,
        ...(params.start_date && { start_date: params.start_date }),
        ...(params.end_date && { end_date: params.end_date }),
      },
    });
    if (response.status !== 202) {
      throw new Error('Unexpected response status');
    }
    return response.data;
  } catch (error) {
    console.error('Error requesting leads download:', error);
    throw error;
  }
};

export const downloadLeadsData = async (params: BookingAnalyticsParams): Promise<DownloadRequestResponse> => {
  try {
    return await requestLeadsDownload(params);
  } catch (error) {
    console.error('Error downloading leads data:', error);
    throw error;
  }
};

export const requestCancellationRescheduleDownload = async (params: BookingAnalyticsParams): Promise<DownloadRequestResponse> => {
  try {
    const response = await apiCallWithRetry({
      method: 'GET',
      url: `${baseURL}/analytics/advance/rc-count/download`,
      params: {
        hospital_id: params.hospital_id,
        ...(params.doctor_id && { doctor_id: params.doctor_id }),
        ...(params.start_date && { start_date: params.start_date }),
        ...(params.end_date && { end_date: params.end_date }),
      },
    });
    if (response.status !== 202) {
      throw new Error('Unexpected response status');
    }
    return response.data;
  } catch (error) {
    console.error('Error requesting cancellation & reschedule download:', error);
    throw error;
  }
};

export const downloadCancellationRescheduleData = async (params: BookingAnalyticsParams): Promise<DownloadRequestResponse> => {
  try {
    return await requestCancellationRescheduleDownload(params);
  } catch (error) {
    console.error('Error downloading cancellation & reschedule data:', error);
    throw error;
  }
};

export const requestCancelReasonDownload = async (params: BookingAnalyticsParams): Promise<DownloadRequestResponse> => {
  try {
    const response = await apiCallWithRetry({
      method: 'GET',
      url: `${baseURL}/analytics/advance/cancel-reason/download`,
      params: {
        hospital_id: params.hospital_id,
        ...(params.doctor_id && { doctor_id: params.doctor_id }),
        ...(params.start_date && { start_date: params.start_date }),
        ...(params.end_date && { end_date: params.end_date }),
      },
    });
    if (response.status !== 202) {
      throw new Error('Unexpected response status');
    }
    return response.data;
  } catch (error) {
    console.error('Error requesting cancel reason download:', error);
    throw error;
  }
};

export const downloadCancelReasonAnalytics = async (params: BookingAnalyticsParams): Promise<DownloadRequestResponse> => {
  try {
    return await requestCancelReasonDownload(params);
  } catch (error) {
    console.error('Error downloading cancel reason analytics:', error);
    throw error;
  }
};

export const requestGoogleReviewDownload = async (params: BookingAnalyticsParams): Promise<DownloadRequestResponse> => {
  try {
    const response = await apiCallWithRetry({
      method: 'GET',
      url: `${baseURL}/analytics/advance/google-review/download`,
      params: {
        hospital_id: params.hospital_id,
        ...(params.doctor_id && { doctor_id: params.doctor_id }),
        ...(params.start_date && { start_date: params.start_date }),
        ...(params.end_date && { end_date: params.end_date }),
      },
    });
    if (response.status !== 202) {
      throw new Error('Unexpected response status');
    }
    return response.data;
  } catch (error) {
    console.error('Error requesting Google review download:', error);
    throw error;
  }
};

export const downloadGoogleReviewData = async (params: BookingAnalyticsParams): Promise<DownloadRequestResponse> => {
  try {
    return await requestGoogleReviewDownload(params);
  } catch (error) {
    console.error('Error downloading Google review data:', error);
    throw error;
  }
};

export const requestRatingDownload = async (params: RatingAnalyticsParams): Promise<DownloadRequestResponse> => {
  try {
    const response = await apiCallWithRetry({
      method: 'GET',
      url: `${baseURL}/analytics/advance/rating/download`,
      params: {
        hospital_id: params.hospital_id,
        ...(params.doctor_id && { doctor_id: params.doctor_id }),
        ...(params.start_date && { start_date: params.start_date }),
        ...(params.end_date && { end_date: params.end_date }),
      },
    });
    if (response.status !== 202) {
      throw new Error('Unexpected response status');
    }
    return response.data;
  } catch (error) {
    console.error('Error requesting rating download:', error);
    throw error;
  }
};

export const downloadRatingAnalytics = async (params: RatingAnalyticsParams): Promise<DownloadRequestResponse> => {
  try {
    return await requestRatingDownload(params);
  } catch (error) {
    console.error('Error downloading rating analytics:', error);
    throw error;
  }
};

export const requestNewPatientsDownload = async (params: BookingAnalyticsParams): Promise<DownloadRequestResponse> => {
  try {
    const response = await apiCallWithRetry({
      method: 'GET',
      url: `${baseURL}/analytics/advance/new-patients/download`,
      params: {
        hospital_id: params.hospital_id,
        ...(params.doctor_id && { doctor_id: params.doctor_id }),
        ...(params.start_date && { start_date: params.start_date }),
        ...(params.end_date && { end_date: params.end_date }),
      },
    });
    if (response.status !== 202) {
      throw new Error('Unexpected response status');
    }
    return response.data;
  } catch (error) {
    console.error('Error requesting new patients download:', error);
    throw error;
  }
};

export const downloadNewPatientsData = async (params: BookingAnalyticsParams): Promise<DownloadRequestResponse> => {
  try {
    return await requestNewPatientsDownload(params);
  } catch (error) {
    console.error('Error downloading new patients data:', error);
    throw error;
  }
};

export const requestShowedUpDownload = async (params: BookingAnalyticsParams): Promise<DownloadRequestResponse> => {
  try {
    const response = await apiCallWithRetry({
      method: 'GET',
      url: `${baseURL}/analytics/advance/showed-up/download`,
      params: {
        hospital_id: params.hospital_id,
        ...(params.doctor_id && { doctor_id: params.doctor_id }),
        ...(params.start_date && { start_date: params.start_date }),
        ...(params.end_date && { end_date: params.end_date }),
      },
    });
    if (response.status !== 202) {
      throw new Error('Unexpected response status');
    }
    return response.data;
  } catch (error) {
    console.error('Error requesting showed up download:', error);
    throw error;
  }
};

export const downloadShowedUpData = async (params: BookingAnalyticsParams): Promise<DownloadRequestResponse> => {
  try {
    return await requestShowedUpDownload(params);
  } catch (error) {
    console.error('Error downloading showed up data:', error);
    throw error;
  }
};

export const requestGoogleReviewsClickedDownload = async (params: BookingAnalyticsParams): Promise<DownloadRequestResponse> => {
  try {
    const response = await apiCallWithRetry({
      method: 'GET',
      url: `${baseURL}/analytics/advance/google-reviews-clicked/download`,
      params: {
        hospital_id: params.hospital_id,
        ...(params.doctor_id && { doctor_id: params.doctor_id }),
        ...(params.start_date && { start_date: params.start_date }),
        ...(params.end_date && { end_date: params.end_date }),
      },
    });
    if (response.status !== 202) {
      throw new Error('Unexpected response status');
    }
    return response.data;
  } catch (error) {
    console.error('Error requesting Google reviews clicked download:', error);
    throw error;
  }
};

export const downloadGoogleReviewsClicked = async (params: BookingAnalyticsParams): Promise<DownloadRequestResponse> => {
  try {
    return await requestGoogleReviewsClickedDownload(params);
  } catch (error) {
    console.error('Error downloading Google reviews clicked data:', error);
    throw error;
  }
};
interface DoctorAttendanceDownloadParams {
  hospital_id: string;
  doctor_mapping_id: string;
  year: number;
}

export const requestDoctorAttendanceDownload = async (params: DoctorAttendanceDownloadParams): Promise<DownloadRequestResponse> => {
  try {
    const response = await apiCallWithRetry({
      method: 'GET',
      url: `${baseURL}/analytics/advance/doctor-attendance/download`,
      params: {
        hospital_id: params.hospital_id,
        doctor_mapping_id: params.doctor_mapping_id,
        year: params.year,
      },
    });
    if (response.status !== 202) {
      throw new Error('Unexpected response status');
    }
    return response.data;
  } catch (error) {
    console.error('Error requesting doctor attendance download:', error);
    throw error;
  }
};

export const downloadDoctorAttendanceData = async (params: DoctorAttendanceDownloadParams): Promise<DownloadRequestResponse> => {
  try {
    return await requestDoctorAttendanceDownload(params);
  } catch (error) {
    console.error('Error downloading doctor attendance data:', error);
    throw error;
  }
};

export const requestAllDoctorsAttendanceDownload = async (params: BookingAnalyticsParams): Promise<DownloadRequestResponse> => {
  try {
    const response = await apiCallWithRetry({
      method: 'GET',
      url: `${baseURL}/analytics/advance/all-doctor-attendance/download`,
      params: {
        hospital_id: params.hospital_id,
        ...(params.start_date && { start_date: params.start_date }),
        ...(params.end_date && { end_date: params.end_date }),
      },
    });
    if (response.status !== 202) {
      throw new Error('Unexpected response status');
    }
    return response.data;
  } catch (error) {
    console.error('Error requesting all doctors attendance download:', error);
    throw error;
  }
};

export const downloadAllDoctorsAttendanceData = async (params: BookingAnalyticsParams): Promise<DownloadRequestResponse> => {
  try {
    return await requestAllDoctorsAttendanceDownload(params);
  } catch (error) {
    console.error('Error downloading all doctors attendance data:', error);
    throw error;
  }
};

// Types for detailed doctor attendance
export interface IndividualSession {
  date: string;
  session_timings: string;
  doctor_checked_in_time: string;
  delay_in_minutes: number;
  status: string;
}

export interface AggregatedDoctorAttendanceData {
  total_sessions_scheduled: number;
  attended_sessions: number;
  unattended_sessions: number;
  disabled_sessions: number;
  total_sessions_absent: number;
}

export interface DoctorDetails {
  name: string;
  specialization: string;
  qualification: string;
  contact_number: string;
}

export interface DetailedDoctorAttendanceResponse {
  individual_sessions: IndividualSession[];
  aggregated_data: AggregatedDoctorAttendanceData;
  doctor_details: DoctorDetails;
}

export interface DetailedDoctorAttendanceParams {
  hospital_id: string;
  doctor_mapping_id: string;
  start_date?: string;
  end_date?: string;
}

export const getDetailedDoctorAttendanceAnalytics = async (
  params: DetailedDoctorAttendanceParams
): Promise<DetailedDoctorAttendanceResponse> => {
  try {
    const { data } = await apiCallWithRetry({
      method: 'GET',
      url: `${baseURL}/analytics/advance/detailed-doctor-attendence`,
      params: {
        hospital_id: params.hospital_id,
        doctor_mapping_id: params.doctor_mapping_id,
        ...(params.start_date && { start_date: params.start_date }),
        ...(params.end_date && { end_date: params.end_date }),
      },
    });
    if (data.status !== "SUCCESS") {
      throw new Error(data.error || "Failed to fetch detailed doctor attendance analytics");
    }
    return data.result;
  } catch (error) {
    console.error('Error fetching detailed doctor attendance analytics:', error);
    throw error;
  }
};

/**
 * Get paginated list of download requests
 */
export const getDownloadRequests = async (
  params: GetDownloadRequestsParams = {}
): Promise<DownloadRequestsResponse> => {
  try {
    const { data } = await apiCallWithRetry({
      method: 'GET',
      url: `${baseURL}/analytics/download-center/requests`,
      params: {
        ...(params.page && { page: params.page }),
        ...(params.limit && { limit: params.limit }),
        ...(params.hospital_id && { hospital_id: params.hospital_id }),
        ...(params.user_id && { user_id: params.user_id }),
        ...(params.status && { status: params.status }),
      },
    });

    console.log("✅✅✅✅")
    console.log(data);

    if (data.status?.toLowerCase() !== "success") {
      throw new Error(data.message || "Failed to fetch download requests");
    }

    return data.result;
  } catch (error) {
    console.error('Error fetching download requests:', error);
    throw error;
  }
};

/**
 * Get download request details by ID
 */
export const getDownloadRequestDetails = async (
  analytics_downloads_id: string
): Promise<DownloadRequest> => {
  try {
    const { data } = await apiCallWithRetry({
      method: 'GET',
      url: `${baseURL}/analytics/download-center/requests/${analytics_downloads_id}`,
    });

    if (data.status?.toLowerCase() !== "success") {
      throw new Error(data.message || "Failed to get download request details");
    }

    return data.result;
  } catch (error) {
    console.error('Error getting download request details:', error);
    throw error;
  }
};

/**
 * Get signed download URL for a specific download request
 */
export const getDownloadSignedUrl = async (
  analytics_downloads_id: string
): Promise<DownloadSignedUrlResponse> => {
  try {
    const { data } = await apiCallWithRetry({
      method: 'GET',
      url: `${baseURL}/analytics/download-center/requests/${analytics_downloads_id}/url`,
    });

    if (data.status?.toLowerCase() !== "success") {
      throw new Error(data.message || "Failed to get download URL");
    }

    return data.result;
  } catch (error) {
    console.error('Error getting download signed URL:', error);
    throw error;
  }
};

/**
 * Search and filter download requests
 */
export const searchDownloadRequests = async (
  params: SearchDownloadRequestsParams = {}
): Promise<DownloadRequestsResponse> => {
  try {
    const { data } = await apiCallWithRetry({
      method: 'GET',
      url: `${baseURL}/analytics/download-center/search`,
      params: {
        ...(params.analytics_type && { analytics_type: params.analytics_type }),
        ...(params.start_date && { start_date: params.start_date }),
        ...(params.end_date && { end_date: params.end_date }),
        ...(params.created_at && { created_at: params.created_at }),
        ...(params.hospital_id && { hospital_id: params.hospital_id }),
        ...(params.page && { page: params.page }),
        ...(params.limit && { limit: params.limit }),
      },
    });

    console.log("✅✅✅✅")
    console.log(data);

    if (data.status?.toLowerCase() !== "success") {
      throw new Error(data.message || "Failed to search download requests");
    }

    return data.result;
  } catch (error) {
    console.error('Error searching download requests:', error);
    throw error;
  }
};

